import type { NameSuggestion, VendorRow } from "@checkone/contract/client";
import { useState } from "react";
import { useApi, useApiMutation } from "../../api/hooks";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useToast } from "../../components/Toasts";
import { ErrorState, Loading } from "../../components/ui";
import { SOURCE_LABEL, formatDay } from "../../lib/format";

function confidenceLabel(c: number): { text: string; tone: string } {
  if (c >= 0.85) return { text: `${Math.round(c * 100)}% name match: likely`, tone: "ok" };
  if (c >= 0.6) return { text: `${Math.round(c * 100)}% name match: possible`, tone: "warn" };
  return { text: `${Math.round(c * 100)}% name match: weak`, tone: "neutral" };
}

/**
 * A vendor with no usable identifier. Name-based candidates are shown as suggestions with their
 * confidence; nothing is applied until the user confirms one (matching is by identifier only).
 */
export function NeedsIdentifierRow({ vendor, onEdit }: { vendor: VendorRow; onEdit: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <tr>
      <td data-label="" colSpan={6}>
        <div className="spread">
          <div>
            <div className="vendor-name">{vendor.display_name}</div>
            <span className="subtle">No NTN or CNIC: excluded from checks until one is added.</span>
          </div>
          <div className="row">
            <button className="btn small" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
              {open ? "Hide suggestions" : "Suggest matches by name"}
            </button>
            <button className="btn small primary" onClick={onEdit}>
              Enter identifier
            </button>
          </div>
        </div>
        {open && <Suggestions vendor={vendor} />}
      </td>
    </tr>
  );
}

function Suggestions({ vendor }: { vendor: VendorRow }) {
  const q = useApi("vendors.suggestions", { params: { id: vendor.id } });
  const accept = useApiMutation("vendors.patch", ["vendors.list", "vendors.history", "vendors.suggestions"]);
  const toast = useToast();
  const [pending, setPending] = useState<NameSuggestion | null>(null);

  if (q.isPending) return <Loading label="Looking for name matches in the FBR lists…" />;
  if (q.error) return <ErrorState error={q.error} />;
  if (q.data.items.length === 0)
    return <p className="muted">No likely matches by name. Ask the vendor for their NTN or CNIC.</p>;

  return (
    <div className="stack" style={{ marginTop: "var(--space-3)" }}>
      <p className="subtle" style={{ margin: 0 }}>
        Suggestions only. Names can match the wrong taxpayer; confirm the identifier with the vendor's
        documents before using it.
      </p>
      {q.data.items.map((s) => {
        const c = confidenceLabel(s.confidence);
        return (
          <div key={s.id} className="suggestion">
            <div className="spread">
              <div>
                <strong>{s.name_as_published}</strong>
                <div className="subtle mono">
                  {s.identifier.type} {s.identifier.display}
                </div>
                <div className="subtle">
                  {SOURCE_LABEL[s.source]} file dated {formatDay(s.file_published_on)}
                </div>
              </div>
              <span className={`pill ${c.tone} confidence`}>{c.text}</span>
            </div>
            <div>
              <button className="btn small" onClick={() => setPending(s)}>
                Use this identifier…
              </button>
            </div>
          </div>
        );
      })}
      <ConfirmDialog
        open={pending !== null}
        title="Use this identifier?"
        confirmLabel="Yes, use it"
        busy={accept.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return;
          accept.mutate(
            {
              params: { id: vendor.id },
              body: {
                accept_suggestion: { suggestion_id: pending.id },
                note: `Accepted name suggestion (${Math.round(pending.confidence * 100)}%)`,
              },
            },
            {
              onSuccess: () => {
                toast.success(`${vendor.display_name} will be checked from the next run.`);
                setPending(null);
              },
              onError: (e) => toast.error(e),
            },
          );
        }}
      >
        {pending && (
          <p>
            Set <strong>{vendor.display_name}</strong>'s {pending.identifier.type} to{" "}
            <span className="mono">{pending.identifier.display}</span> (FBR name: {pending.name_as_published}
            ). This is recorded in the vendor's history with your name.
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}
